import { decrypt } from '@/common/securityUtils';
import { Form, useForm } from '@/components/forms';
import RadioGroup from '@/components/forms/RadioGroup';
import { nameValidationRegex } from '@/constants/regex';
import { Button } from '@/design-system/button';
import { useDynamicLocale } from '@/hoslog-intl';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useManageBankInformation } from '../hooks/useManageBankInformation';
import { AddBankInfoOptions, AddBankType } from './admin.constant';
import { OtherBankFields } from './OtherBankFields';
import { PostBankFields } from './PostBankFields';

const formSchema = z
  .object({
    bankType: z.string().trim().min(1, { message: 'common.form.validation.required' }),
    bankName: z.string().trim().optional(),
    branchCode: z.string().trim().optional(),
    accountSymbol: z.string().trim().optional(),
    accountNumber: z.string().trim().regex(/^\d+$/, { message: 'common.form.validation.invalid' }),
    accountName: z
      .string()
      .trim()
      .min(1, { message: 'common.form.validation.required' })
      .regex(nameValidationRegex, { message: 'common.form.validation.nameOnly' }),
    branchName: z
      .string()
      .trim()
      .min(1, { message: 'common.form.validation.required' })
      .regex(nameValidationRegex, { message: 'common.form.validation.nameOnly' }),
  })
  .superRefine((data, ctx) => {
    const isPostalBank = decrypt(data.bankType) === AddBankType.PostalBank;
    if (!isPostalBank) {
      if (!data.bankName?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankName'],
          message: 'common.form.validation.required',
        });
      } else if (!nameValidationRegex.test(data.bankName.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bankName'],
          message: 'common.form.validation.nameOnly',
        });
      }
    }
    if (isPostalBank) {
      if (!data.accountSymbol?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['accountSymbol'],
          message: 'common.form.validation.required',
        });
      }
    }
  });

export type AddBankInfoDefaultValues = z.input<typeof formSchema>;

interface AddBankInfoFormProps {
  defaultValues: AddBankInfoDefaultValues;
}

export const AddBankInfoForm = ({ defaultValues }: AddBankInfoFormProps) => {
  const navigate = useNavigate();
  const { t } = useDynamicLocale();
  const { mutateAsync, isPending: isSaving } = useManageBankInformation();

  const form = useForm({
    schema: formSchema,
    mode: 'onBlur',
    defaultValues,
  });

  const bankType = useWatch({
    control: form.control,
    name: 'bankType',
  });

  useEffect(() => {
    form.clearErrors(['bankName', 'accountSymbol']);
  }, [bankType, form]);

  const isPostalBank = decrypt(bankType) === AddBankType.PostalBank;
  const bankTypeOptions = [
    {
      ...AddBankInfoOptions[0],
      label: t('paymentManagement.adminPaymentSetup.adminBankInfo.other'),
    },
    {
      ...AddBankInfoOptions[1],
      label: t('paymentManagement.adminPaymentSetup.adminBankInfo.postBank'),
    },
  ];

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    await mutateAsync({
      bankType: values.bankType,
      bankName: values.bankName?.trim() ?? '',
      branchName: values.branchName?.trim() ?? '',
      branchCode: values.branchCode?.trim() ?? '',
      accountNumber: Number(values.accountNumber),
      accountSymbol: values.accountSymbol?.trim() ?? '',
      accountName: values.accountName?.trim() ?? '',
    });
    await navigate({ to: '/payment-management/admin-payment-setup' });
  };

  return (
    <Form form={form} onSubmit={handleSubmit}>
      <div className="mx-auto w-full max-w-5xl rounded-lg bg-card p-6 shadow-sm md:p-8">
        <h3 className="mb-6 border-b border-border-divider pb-3 text-base font-bold text-foreground-heading">
          {t('paymentManagement.adminPaymentSetup.adminBankInfo.title')}
        </h3>
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-12 xl:gap-20">
          <div className="w-full shrink-0 lg:w-auto pl-[36px]">
            <RadioGroup
              variant="default"
              groupLabel=""
              options={bankTypeOptions}
              name="bankType"
              label={t('paymentManagement.adminPaymentSetup.adminBankInfo.bankType')}
              optionsClassName="flex-wrap sm:flex-nowrap gap-6"
            />
          </div>
          <div className="w-full min-w-0 flex-1">{isPostalBank ? <PostBankFields /> : <OtherBankFields />}</div>
        </div>
        <div className="mt-6 flex w-full justify-between border-t border-border-divider pt-6">
          <Button variant="outline" type="button" className="h-10 px-4 text-base font-bold text-secondary" asChild>
            <Link to="/payment-management/admin-payment-setup">{t('common.page.button.cancel')}</Link>
          </Button>
          <Button type="submit" className="h-10 px-4 text-base font-bold" isLoading={isSaving}>
            {t('common.page.button.submit')}
          </Button>
        </div>
      </div>
    </Form>
  );
};
